// Derives a relative value for information from how it is used, so the information lens doesn't depend on every
// output being scored by hand.
//
// Infonomics treats information as a non-rivalrous asset: using it in more places doesn't use it up, so the more
// actions, models and outcomes it reaches, and the more revenue flows back to it, the more it is worth. That is what
// the derived value reads from the graph. It is a ranking from 0 to 100 across the organisation, per item, so it can
// be compared with (and suggested as) an output's value per item. It never replaces a value the user has set.
//
// Shared with linq-server (copied by its `npm run copy`), so it must only import from this folder.
import { EDGE_TYPE, NODE_SUPERTYPE, NODE_TYPE, OUTPUT_VALUE_SOURCE, SYSTEM_KIND } from "./linqset-base"
import { getInsights, Linqset, LinqsetEdge, LinqsetNode, NodeInsights } from "./linqset-model"
import type { AssetValue, ModelInsights } from "./organisation"

// the weight of each signal in the derived value. Revenue reaching the information counts most; the rest read reuse
export const DERIVED_VALUE_WEIGHTS = {
    revenue: 4,         // revenue per item that flows back to the information from the outputs and outcomes it leads to
    value: 2,           // value per item set by users that flows back to it
    outcomes: 2,        // outcomes it reaches, however far downstream
    reuse: 2,           // actions and outcomes that use it directly (reuse), in this model and others
    models: 1,          // other models it is used in
    external: 1,        // external organisations performing the actions that use it
}
type Signal = keyof typeof DERIVED_VALUE_WEIGHTS

// edges from information to what uses it directly
const USE_EDGES = [EDGE_TYPE.SUPPORT, EDGE_TYPE.JOINS, EDGE_TYPE.USED_IN, EDGE_TYPE.REALISE]
// edges that carry information forward through the process
const FLOW_EDGES = [EDGE_TYPE.CREATE, ...USE_EDGES]

interface Information {
    node:LinqsetNode
    insights:NodeInsights
}

// the information each linqset owns (a filter owns everything it shows), with its insights for that model, once each
const informationIn = (linqsets:Linqset[]):Information[] => {
    const seen = new Set<string>()
    const information:Information[] = []
    linqsets.forEach(linqset => {
        linqset.nodes
            .filter(n => n.superType === NODE_SUPERTYPE.INFORMATION && (linqset.isFilterModel() || n.modelKey === linqset.modelKey))
            .forEach(n => {
                if (seen.has(n._key)) return
                seen.add(n._key)
                information.push({node: n, insights: getInsights(n, linqset)})
            })
    })
    return information
}

const groupBy = <T>(items:T[], key:(item:T) => string) => {
    const map = new Map<string, T[]>()
    items.forEach(item => {
        const k = key(item)
        const list = map.get(k)
        if (list) list.push(item)
        else map.set(k, [item])
    })
    return map
}

// a value or revenue per item set on the output, typed by the user or accepted from LINQ's or AI's suggestion
export const hasOutputValueSet = (node:LinqsetNode|undefined) =>
    (node?.properties?.outputValue || 0) > 0 || (node?.properties?.outputRevenue || 0) > 0

// a value or revenue set on the output that says what it's worth independently of LINQ's derived value: typed, or AI's
// suggestion. A derived value accepted as the output's value is LINQ's own reading, so counting it would feed the
// derived value back into itself
const valuedIndependently = (node:LinqsetNode|undefined) =>
    ((node?.properties?.outputValue || 0) > 0 && node?.properties?.outputValueSource !== OUTPUT_VALUE_SOURCE.DERIVED) ||
    (node?.properties?.outputRevenue || 0) > 0

// the nodes of a workspace, and the edges information flows along and actions are performed by. Realising an outcome
// is only use when the output has a value or revenue set (other than its accepted derived value): linking an output to
// an outcome says nothing of its worth until someone values it
interface UsageGraph {
    nodes:Map<string, LinqsetNode>
    outgoing:Map<string, LinqsetEdge[]>
    performers:Map<string, LinqsetEdge[]>
}
const usageGraph = (linqsets:Linqset[]):UsageGraph => {
    const nodes = new Map<string, LinqsetNode>()
    const edges = new Map<string, LinqsetEdge>()
    linqsets.forEach(l => {
        l.nodes.forEach(n => nodes.set(n._key, n))
        l.edges.forEach(e => edges.set(e._key, e))
    })
    const all = [...edges.values()]
        .filter(e => e.type !== EDGE_TYPE.REALISE || valuedIndependently(nodes.get(e.sourceKey)))
    return {
        nodes,
        outgoing: groupBy(all.filter(e => FLOW_EDGES.includes(e.type)), e => e.sourceKey),
        performers: groupBy(all.filter(e => e.type === EDGE_TYPE.PERFORM), e => e.targetKey),
    }
}

// what one information node reaches, as keys, so the reach of several (an asset's states) can be joined
interface Reach {
    consumers:Set<string>
    consumerModels:Set<string>
    outcomes:Set<string>
}
const reachOf = (node:LinqsetNode, graph:UsageGraph):Reach => {
    const uses = (graph.outgoing.get(node._key) || []).filter(e => USE_EDGES.includes(e.type))
    // a join or used-in edge belongs to the model that receives the information
    const consumerModels = uses
        .filter(e => e.type === EDGE_TYPE.JOINS || e.type === EDGE_TYPE.USED_IN)
        .map(e => e.modelKey || graph.nodes.get(e.targetKey)?.modelKey)
        .filter(m => m && m !== node.modelKey) as string[]

    // everything downstream, once each, however the paths split, join or loop
    const reached = new Set<string>()
    const queue = [node._key]
    while (queue.length > 0) {
        const key = queue.shift() as string
        ;(graph.outgoing.get(key) || []).forEach(e => {
            if (reached.has(e.targetKey)) return
            reached.add(e.targetKey)
            queue.push(e.targetKey)
        })
    }
    return {
        consumers: new Set(uses.map(e => e.targetKey)),
        consumerModels: new Set(consumerModels),
        outcomes: new Set([...reached].filter(k => graph.nodes.get(k)?.superType === NODE_SUPERTYPE.OUTCOMES)),
    }
}

// the external organisations performing the given actions
const externalsOf = (consumers:Set<string>, graph:UsageGraph) => new Set([...consumers]
    .flatMap(k => (graph.performers.get(k) || []).map(e => graph.nodes.get(e.sourceKey)))
    .filter(n => n?.type === NODE_TYPE.EXTERNAL_ORGANISATION)
    .map(n => (n as LinqsetNode)._key))

/**
 * Reads how each information node is used: the actions and outcomes that use it directly, the other models it is
 * used in, the external organisations performing those actions, and the outcomes it reaches however far downstream.
 * Run over every model of a workspace together, so use in other models is seen. An output's consumers are in other
 * models or are the outcomes it realises, as it has nothing after it in its own model.
 */
export const setInformationReach = (linqsets:Linqset[]) => {
    const graph = usageGraph(linqsets)
    informationIn(linqsets).forEach(({node, insights}) => {
        const reach = reachOf(node, graph)
        insights.consumers = reach.consumers.size
        insights.consumerModels = reach.consumerModels.size
        insights.externalConsumers = externalsOf(reach.consumers, graph).size
        insights.outcomesReached = reach.outcomes.size
    })
}

// an output's work is friction when nothing values it: no value or revenue flows to it, and nothing uses it. An
// output that is used downstream carries a derived value, so it isn't friction even when no one has scored it
export const isFrictionOutput = (insights:NodeInsights) =>
    insights.value === 0 && insights.revenue === 0 && !(insights.consumers > 0)

type Signals = {[signal in Signal]:number}
const perItem = (amount:number|undefined, items:number|undefined) => (items || 0) > 0 ? (amount || 0) / (items as number) : 0

// the signals behind a derived value from per-item amounts and counts, so volume doesn't swamp reuse
const signalsOf = (revenuePerItem:number, valuePerItem:number, outcomes:number, consumers:number, models:number, externals:number):Signals => ({
    revenue: revenuePerItem,
    value: valuePerItem,
    outcomes,
    // reuse counts for less and less the more places it is used, so fan-out can't be gamed
    reuse: Math.log(1 + consumers),
    models: Math.log(1 + models),
    external: Math.log(1 + externals),
})

// the value set on an output itself, as against value flowing back to it from what it leads to
const ownValue = (node:LinqsetNode|undefined, insights:NodeInsights) =>
    insights.joinedOut ? 0 : (node?.properties?.outputValue || 0) * (insights.numberProcessed || 0)

// the signals behind an information node's derived value. The value it reads is what flows back to the information from
// the outputs and outcomes it leads to, not a value set on the information itself: the derived value is offered as that
// value, so reading it would make each one accepted raise the next
export const derivedValueSignals = (insights:NodeInsights, node?:LinqsetNode):Signals =>
    signalsOf(perItem(insights.revenue, insights.numberProcessed), perItem(Math.max(0, (insights.value || 0) - ownValue(node, insights)), insights.numberProcessed),
        insights.outcomesReached || 0, insights.consumers || 0, insights.consumerModels || 0, insights.externalConsumers || 0)

/**
 * Ranks things by their signals: a weighted score of each, normalised against the highest of the things ranked,
 * scaled so the most valuable is 100. A signal none of them has (for example revenue, in an organisation with none) is
 * left out rather than dragging every score down. Anything with no signal is 0, and anything with one at least 1, so
 * it is never mistaken for a dead end.
 */
const rank = (signals:Signals[]):number[] => {
    const names = Object.keys(DERIVED_VALUE_WEIGHTS) as Signal[]
    const highest = Object.fromEntries(names.map(s => [s, Math.max(0, ...signals.map(v => v[s]))])) as Signals
    const available = names.filter(s => highest[s] > 0)
    const weight = available.reduce((t, s) => t + DERIVED_VALUE_WEIGHTS[s], 0)

    const scores = signals.map(v => weight > 0 ? available.reduce((t, s) => t + DERIVED_VALUE_WEIGHTS[s] * v[s] / highest[s], 0) / weight : 0)
    const top = Math.max(0, ...scores)
    return scores.map(score => top > 0 && score > 0 ? Math.max(1, Math.round(100 * score / top)) : 0)
}

/**
 * Sets each information node's derived value, ranked across the information of the organisation. Run after values
 * have been assigned, over every model of a workspace together.
 */
export const deriveInformationValues = (linqsets:Linqset[]) => {
    const information = informationIn(linqsets)
    const values = rank(information.map(i => derivedValueSignals(i.insights, i.node)))
    information.forEach((i, n) => i.insights.derivedValue = values[n])
}

/**
 * Works out how each information asset is used, across all of its states, and the value derived from that, ranked
 * across the assets of the organisation on their own scale (100 is the most valuable asset), so the derived value of
 * each output is unchanged. An asset is the information itself and its states are the stages it passes through, so:
 * - the work moving it from one state to another isn't reuse: an action using one of its states to create another
 *   isn't counted as a consumer
 * - models it has states in aren't other models using it
 * - value and revenue flow back along the chain to its earlier states, so its value per item is the highest of any
 *   state, not their sum
 * Run after deriveInformationValues, over every model of a workspace together. Returns the figures by asset key.
 */
export const deriveAssetValues = (linqsets:Linqset[]):Map<string, AssetValue> => {
    const graph = usageGraph(linqsets)
    const assets = groupBy(informationIn(linqsets).filter(i => i.node.informationAssetKey), i => i.node.informationAssetKey as string)
    const assetOf = (key:string) => graph.nodes.get(key)?.informationAssetKey

    const figures = [...assets.entries()].map(([assetKey, states]) => {
        const reach = states.map(s => reachOf(s.node, graph))
        const ownModels = new Set(states.map(s => s.node.modelKey))
        const movesIt = (key:string) => (graph.outgoing.get(key) || []).some(e => e.type === EDGE_TYPE.CREATE && assetOf(e.targetKey) === assetKey)
        const consumers = new Set(reach.flatMap(r => [...r.consumers]).filter(k => !movesIt(k)))
        const consumerModels = new Set(reach.flatMap(r => [...r.consumerModels]).filter(m => !ownModels.has(m)))
        const outcomes = new Set(reach.flatMap(r => [...r.outcomes]))
        return {
            assetKey,
            value: {
                consumers: consumers.size,
                consumerModels: consumerModels.size,
                externalConsumers: externalsOf(consumers, graph).size,
                outcomesReached: outcomes.size,
                valuePerItem: Math.max(0, ...states.map(s => perItem(s.insights.value, s.insights.numberProcessed))),
                revenuePerItem: Math.max(0, ...states.map(s => perItem(s.insights.revenue, s.insights.numberProcessed))),
                derivedValue: 0,
            } as AssetValue,
        }
    })
    const values = rank(figures.map(({value: v}) =>
        signalsOf(v.revenuePerItem, v.valuePerItem, v.outcomesReached, v.consumers, v.consumerModels, v.externalConsumers)))
    figures.forEach((f, n) => f.value.derivedValue = values[n])
    return new Map(figures.map(f => [f.assetKey, f.value]))
}

// puts each asset's figures on the models' insights for it
export const setAssetValues = (insights:ModelInsights[], values:Map<string, AssetValue>) =>
    insights.forEach(i => (i.informationAssets || []).forEach(a => a.assetValue = values.get(a.groupKey)))

// how an output is held, read from its type and the systems attached to it. It sits beside the derived value rather
// than in it: the derived value ranks how information is used, and a valuable output kept on paper is still valuable,
// just at risk.
// - typed as a file, on paper or by word of mouth, it's held that way, whatever system it came from
// - otherwise a system attached to it says where: the system of record, a business system or a local tool (a spreadsheet,
//   a document, email), the best of them where there are several
// - typed as held in a system with none attached, it's in a system, but which kind isn't known
// - typed as plain information, with no system attached, it says nothing about how it is held
export type InformationHeld = 'record' | 'business' | 'local' | 'system' | 'file' | 'paper' | 'verbal' | 'unsaid'
export const INFORMATION_HELD_LABELS: Record<InformationHeld, string> = {
    record: 'In a system of record',
    business: 'In a business system',
    local: 'In a local tool',
    system: 'In a system',
    file: 'As a file',
    paper: 'On paper',
    verbal: 'By word of mouth',
    unsaid: 'Not said',
}
// the types saying information is held in a system, and those saying it's held outside any
export const SYSTEM_TYPES = new Set([NODE_TYPE.APPLICATION, NODE_TYPE.SERVICE, NODE_TYPE.DATABASE])
export const MEDIUM_TYPES = new Set([NODE_TYPE.FILE, NODE_TYPE.PAPER, NODE_TYPE.VERBAL])
export const informationHeld = (type: NODE_TYPE | undefined, systemKinds: SYSTEM_KIND[] = []): InformationHeld => {
    switch (type) {
        case NODE_TYPE.FILE: return 'file'
        case NODE_TYPE.PAPER: return 'paper'
        case NODE_TYPE.VERBAL: return 'verbal'
    }
    if (systemKinds.includes(SYSTEM_KIND.RECORD)) return 'record'
    if (systemKinds.includes(SYSTEM_KIND.BUSINESS)) return 'business'
    if (systemKinds.includes(SYSTEM_KIND.LOCAL)) return 'local'
    return type !== undefined && SYSTEM_TYPES.has(type) ? 'system' : 'unsaid'
}
// held outside any system with controls around it: in a local tool, a file, on paper or by word of mouth
export const heldOutsideSystems = (held: InformationHeld) => held === 'local' || held === 'file' || held === 'paper' || held === 'verbal'

// the share of the derived value (per item, times the items a year) held outside any system, as a fraction, and the
// outputs that carry it. Outputs whose holding isn't said are left out of both sides, and counted
export const derivedValueOutsideSystems = (outputs: { held: InformationHeld; derivedValue: number; items: number }[]) => {
    const weightOf = (o: { derivedValue: number; items: number }) => (o.derivedValue || 0) * Math.max(o.items || 0, 1)
    const said = outputs.filter(o => o.held !== 'unsaid' && o.derivedValue > 0)
    const outside = said.filter(o => heldOutsideSystems(o.held))
    const total = said.reduce((t, o) => t + weightOf(o), 0)
    return {
        share: total > 0 ? outside.reduce((t, o) => t + weightOf(o), 0) / total : 0,
        outputs: outside.length,
        unsaid: outputs.filter(o => o.held === 'unsaid' && o.derivedValue > 0).length,
    }
}
